import React, { useCallback, useEffect, useState } from "react";

import { getResiduePayoutPotential } from "services";

import {
  filterOptionsWithSynonyms,
  formatNumericCurrency,
  generateRandomId,
  useAppType,
  useGetCarbonProgramByFarmId,
  useTranslation,
  useUserContext,
} from "utils";

import { PracticeCrop, RedzoneException, WithInjectedName } from "models";

import {
  ExtraInfo,
  Flex,
  Form,
  GuidanceLink,
  Icon,
  InputNumber,
  KlimRadioGroup,
  OptGroup,
  Option,
  Select,
  Spin,
  Tooltip,
  Message as message,
} from "components";

type CropFormEntry = {
  cropId: string;
  residue: boolean;
  mandatory: boolean;
  mainCrop: boolean;
  yield?: number;
};

type SelectedCrop = {
  cropId: string | null;
  key: number;
  mainCrop: boolean;
  mandatory: boolean | "true" | "false" | null;
  redzoneExceptionId: string | null;
  residue: boolean | "true" | "false" | null;
  neutral?: boolean | "true" | "false" | null;
  yield?: number | null;
};

type CropFormProps = {
  cropId: string | null;
  crops: Array<WithInjectedName<PracticeCrop>>;
  forms: { current: any }; // eslint-disable-line @typescript-eslint/no-explicit-any
  index: number;
  mainCrop: boolean;
  moveDown: (index: number) => void;
  moveUp: (index: number) => void;
  selectedCrops: SelectedCrop[];
  topCrops: Array<WithInjectedName<PracticeCrop>>;
  year: number;
  onDelete: (index: number) => void;
  onUpdate: (index: number, value: CropFormEntry) => void;
  fieldIsInRedzone?: boolean;
  hectares?: number;
  regionId?: string;
  fieldId?: string;
  redzoneExceptions?: RedzoneException[];
  showErrors?: boolean;
  dataTestId?: string;
  isAdmin?: boolean;
  onCropChange?: (v: string, selectedCrops: SelectedCrop[]) => void;
  showMultipleMainCropsWarning?: boolean;
};

const getInitialResidueValue = (residue?: boolean | "true" | "false" | null) => {
  if (typeof residue === "undefined" || residue === null) {
    return null;
  }

  if (typeof residue === "boolean") {
    return residue.toString();
  }

  const parsedResidue = JSON.parse(residue);

  if (parsedResidue === true) {
    return "true";
  }

  if (parsedResidue === false) {
    return "false";
  }

  return null;
};

export const CropsForm = ({
  cropId,
  crops,
  forms,
  index,
  mainCrop,
  moveDown,
  moveUp,
  selectedCrops,
  topCrops,
  year,
  onDelete,
  onUpdate,
  fieldIsInRedzone,
  hectares,
  regionId,
  fieldId,
  redzoneExceptions,
  showErrors = true,
  dataTestId,
  isAdmin = false,
  onCropChange,
  showMultipleMainCropsWarning,
}: CropFormProps) => {
  const { t } = useTranslation(["modules"]);
  const { mobile } = useAppType();
  const [selectedCrop, setSelectedCrop] = useState<SelectedCrop | undefined>(selectedCrops[index]);
  const [showNeutralCropsInfoDrawer, setShowNeutralCropsInfoDrawer] = useState(false);
  const [enabled, setEnabled] = useState(true);

  const { user } = useUserContext();
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(user?.farm?.id ?? undefined);

  const mandatoryCropRadioGroupOptions = [
    { label: t("bauEntryDrawer.crops.redzoneException.yes"), value: "true" },
    { label: t("bauEntryDrawer.crops.redzoneException.no"), value: "false" },
  ];

  const showRedzoneExceptionQuestion = !mainCrop && fieldIsInRedzone && crops?.find((crop) => crop.id === cropId)?.isCatchCrop;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (index: number, value: any) => {
    const nextCrop = crops.find(({ id }) => id === value.cropId);
    if (!nextCrop) return;

    if (fieldIsInRedzone) {
      // reset redzone values on crop change
      if (value.cropId !== cropId) {
        value.mandatory = nextCrop?.isCatchCrop ? "true" : null;
        value.redzoneExceptionId = null;
        const cropDetails = crops?.find((item) => item.id === value.cropId);
        setEnabled(!!cropDetails?.enabled);
      }
      // remove redzoneExceptionId if the crop is not mandatory
      else if (value.mandatory === "true" && (selectedCrop?.mandatory === "true" || selectedCrop?.mandatory === true)) {
        value.redzoneExceptionId = null;
      }
    }

    setSelectedCrop(value);

    // The form sends { residue: "true" } or { residue: "false" } as string
    // rather than a boolean, and since Boolean("false") resolves to `true`
    // we have to explicitely check against the string to have crop residues
    // update correctly

    let residue;

    // Reset residue on crop change
    if (value.cropId !== cropId && nextCrop.residue) {
      residue = null;
    } else if (nextCrop.residue) {
      residue = value.residue === "true";
    } else {
      residue = false;
    }

    onUpdate(index, {
      ...value,
      residue,
      mandatory: value.mandatory === "true",
      mainCrop,
    });
  };

  // commented out for CCC-192
  // useEffect(() => {
  //   if (!selectedCrop || !selectedCrop.cropId || selectedCrop.cropId === "null" || type !== "yearly") {
  //     setLoading(false);
  //     return;
  //   }

  //   const selectedCropsInCrops = selectedCrops.map((crop) => crops.find(({ id }) => id === crop.cropId));
  //   const nonNeutralCropCount = selectedCropsInCrops.filter((crop) => !crop?.neutral)?.length;

  //   setLoading(true);

  //   const promises = [getResiduePayoutPotential({ cropId, hectares, regionId })];
  //   if (fieldIsInRedzone) {
  //     promises.push(
  //       getRedzoneCoverCropPayoutPotential({
  //         cropId,
  //         hectares,
  //         regionId,
  //         nonNeutralCropCount,
  //         residue: selectedCrop.residue,
  //         signal,
  //       })
  //     );
  //   }

  //   Promise.all(promises)
  //     .then(([residuePayoutRes, coverCropPayoutRes]) => {
  //       setResiduePayout(residuePayoutRes?.residuePayout);
  //       setMandatoryCoverCropPayout(coverCropPayoutRes?.mandatoryPayout);
  //       setNotMandatoryCoverCropPayout(coverCropPayoutRes?.notMandatoryPayout);
  //     })
  //     .finally(() => {
  //       setLoading(false);
  //     });
  // }, [selectedCrop, selectedCrops]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cropId && crops && selectedCrops?.length) {
      const cropDetails = crops?.find((item) => item.id === cropId);
      setSelectedCrop(selectedCrops.find((crop) => crop.cropId === cropId));
      setEnabled(!!cropDetails?.enabled);
    }
  }, [cropId, crops, selectedCrops]);

  useEffect(() => {
    if (!enabled) {
      message({ content: t("bauEntryDrawer.updateRequiredMessage"), icon: "error" });
    }
  }, [enabled, t]);

  const [residuePayout, setResiduePayout] = useState<number | null>(null);

  const getResiduePayouts = useCallback(
    async (cropId: string) => {
      if (!hectares || (!regionId && !fieldId)) {
        setResiduePayout(null);
        return;
      }

      const hasResidue = crops?.find((crop) => crop.id === cropId)?.residue;
      if (!hasResidue) {
        setResiduePayout(null);
        return;
      }

      const residuePayout = await getResiduePayoutPotential({
        cropId,
        hectares,
        regionId,
        fieldId,
      });

      setResiduePayout(residuePayout.residuePayoutMax);
    },
    [hectares, regionId, fieldId, crops]
  );

  useEffect(() => {
    if (!cropId) return;
    getResiduePayouts(cropId);
  }, [cropId, getResiduePayouts]);

  return (
    <>
      <div className="form-product">
        <Form
          name={year ? `existing-${year}-${index}-${generateRandomId()}` : `existing-${index}-${generateRandomId()}`}
          ref={year !== undefined ? (el) => (forms.current[year][index] = el) : (el) => (forms.current[index] = el)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onValuesChange={(_: any, v: any) => handleChange(index, v)}
          className={!showErrors ? "no-errors" : ""}
          dataTestId={dataTestId}
        >
          <div className="form-input-container">
            <div className="crop-input-container">
              {selectedCrops.length > 1 && (
                <div className="chevrons">
                  {index !== 0 && <Icon iconName="tabler/chevron-up" className="pointer" onClick={() => moveUp(index)} />}
                  {index !== selectedCrops.length - 1 && <Icon iconName="tabler/chevron-down" className="pointer" onClick={() => moveDown(index)} />}
                </div>
              )}
              <div className="full-width">
                <div className="crops-title">
                  <h6>
                    <span className="margin-right-25">{t(mainCrop ? "bauEntryDrawer.crops.mainCrop.title" : "bauEntryDrawer.crops.sideCrop.title")}</span>
                    <Tooltip placement="bottom" title={t(mainCrop ? "bauEntryDrawer.crops.mainCrop.tooltip" : "bauEntryDrawer.crops.sideCrop.tooltip")}>
                      <Icon size="s" iconName="tabler/info-circle" className="icon inline pointer" />
                    </Tooltip>
                  </h6>
                  {(!mainCrop || showMultipleMainCropsWarning) && (
                    <Icon dataTestId="crops-form-trash" iconName="tabler/trash" className="icon inline pointer" onClick={() => onDelete(index)} />
                  )}
                </div>
                {selectedCrop?.neutral ? (
                  <GuidanceLink
                    iconSize="s"
                    variant="grey"
                    text={t("bauEntryDrawer.crops.neutralCropInfo")}
                    onClick={() => setShowNeutralCropsInfoDrawer(true)}
                  />
                ) : null}
              </div>
            </div>
            <div className="crop-input-container">
              {selectedCrops.length > 1 && <div className="empty" />}
              <div className={selectedCrops.length > 1 ? "crop-input-narrow" : "full-width"}>
                {/* @ts-expect-error need to convert to tsx */}
                <Select
                  dataTestId={`${dataTestId}-top-crop-selection`}
                  label={mainCrop ? t("bauEntryDrawer.crops.mainCrop.title") : t("bauEntryDrawer.crops.sideCrop.title")}
                  name="cropId"
                  initialValue={cropId}
                  rules={[{ required: true, message: false }]}
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOptionsWithSynonyms}
                  className={!enabled ? "disabled-property" : ""}
                  onChange={(v) => (isAdmin && onCropChange ? onCropChange(v, selectedCrops) : null)}
                >
                  {topCrops && topCrops.length && (
                    <OptGroup key="top" label={t("bauEntryDrawer.crops.mostCommon")}>
                      {topCrops
                        .filter((option) => !option.onlyVisibleToAdmins)
                        .map((crop, index) => (
                          <Option
                            data-testid={`${dataTestId}-top-crop-selection-option-${index}`}
                            key={`top-${crop.id}`}
                            label={crop.synonyms}
                            value={crop.id}
                            // disable crops that are already selected for this year
                            disabled={!!selectedCrops.find(({ cropId }) => cropId === crop.id)}
                          >
                            {crop.name}
                          </Option>
                        ))}
                    </OptGroup>
                  )}
                  <OptGroup key="all" label={t("bauEntryDrawer.crops.allCrops")}>
                    {crops
                      ?.sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                      // If the initial value is of a disabled crop we want to render it,
                      // all other disabled ones not
                      .filter((option) => option.enabled || option.id === cropId)
                      .map((option) => (
                        <Option
                          key={`all-${option.id}`}
                          label={option.synonyms}
                          value={option.id}
                          // disable crops that are already selected for this year
                          disabled={!!selectedCrops.find(({ cropId }) => cropId === option.id)}
                        >
                          {option.name}
                        </Option>
                      ))}
                  </OptGroup>
                </Select>
                {!enabled && <span className="disabled-property-message">{t("bauEntryDrawer.crops.disabledMessage")}</span>}
              </div>
            </div>
            {crops?.find((crop) => crop.id === cropId)?.residue ? (
              <div className="residue-input-container crop-input-container">
                {selectedCrops.length > 1 && <div className="empty" />}
                <Flex vertical={mobile} justify="space-between" gap="1rem" className="full-width">
                  <span className="paragraph-m residue-input-title">{t("bauEntryDrawer.crops.residueLeft.title")}</span>
                  <Form.Item
                    name="residue"
                    rules={[{ required: true, message: t("bauEntryDrawer.crops.residueLeft.error") }]}
                    initialValue={getInitialResidueValue(selectedCrop?.residue)}
                  >
                    <KlimRadioGroup
                      size="large"
                      type="tertiary"
                      data-testid="residue-result-toggle"
                      options={[
                        {
                          value: "true",
                          label: (
                            <Flex vertical justify="space-around">
                              <span>{t("bauEntryDrawer.crops.residueLeft.yes")}</span>
                              {typeof residuePayout === "number" && residuePayout !== 0 && (
                                <span className="paragraph-xs grey-text">{`${t("bauEntryDrawer.crops.upToPayoutPrediction")} ${formatNumericCurrency(
                                  residuePayout,
                                  { currency: carbonProgram?.currency, decimals: 0, signDisplay: "always" }
                                )}`}</span>
                              )}
                            </Flex>
                          ),
                        },
                        {
                          value: "false",
                          label: (
                            <Flex vertical justify="space-around">
                              <span>{t("bauEntryDrawer.crops.residueLeft.no")}</span>
                              {typeof residuePayout === "number" && residuePayout !== 0 && (
                                <span className="paragraph-xs grey-text">{formatNumericCurrency(0)}</span>
                              )}
                            </Flex>
                          ),
                        },
                      ]}
                      value={selectedCrop?.residue}
                      optionType="button"
                      buttonStyle="solid"
                      fullWidth
                    />
                  </Form.Item>
                </Flex>
              </div>
            ) : null}

            {showRedzoneExceptionQuestion && (
              <div className="residue-input-container crop-input-container">
                {selectedCrops.length > 1 && <div className="empty" />}
                <div className="row-space-between">
                  <span className="paragraph-m margin-bottom-50 margin-right-100">{t("bauEntryDrawer.crops.redzoneException.title")}</span>
                  <Form.Item
                    name="mandatory"
                    rules={[{ required: true, message: t("bauEntryDrawer.crops.redzoneException.error") }]}
                    initialValue={selectedCrop?.mandatory === "false" || selectedCrop?.mandatory === false ? `${selectedCrop?.mandatory}` : "true"}
                  >
                    <KlimRadioGroup
                      size="large"
                      type="tertiary"
                      data-testid="mandatory-crop-result-toggle"
                      options={mandatoryCropRadioGroupOptions}
                      value={selectedCrop?.mandatory}
                      optionType="button"
                      buttonStyle="solid"
                    ></KlimRadioGroup>
                  </Form.Item>
                </div>
              </div>
            )}
            {showRedzoneExceptionQuestion && (selectedCrop?.mandatory === "false" || selectedCrop?.mandatory === false) && (
              <div className="crop-input-container">
                {selectedCrops.length > 1 && <div className="empty" />}
                <div className="full-width">
                  <span className="paragraph-m margin-bottom-100 margin-right-100">{t("bauEntryDrawer.crops.notObligated.title")}</span>
                  <Form.Item
                    name="redzoneExceptionId"
                    rules={[{ required: true, message: t("bauEntryDrawer.crops.redzoneException.error") }]}
                    initialValue={selectedCrops[index].redzoneExceptionId !== null ? `${selectedCrops[index].redzoneExceptionId}` : null}
                  >
                    {redzoneExceptions?.length ? (
                      // @ts-expect-error need to convert to tsx
                      <Select
                        className="margin-top-50"
                        dataTestId="redzone-exception-select"
                        label={t("bauEntryDrawer.crops.notObligated.label")}
                        rules={[{ required: true, message: false }]}
                        optionFilterProp="children"
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        filterOption={(input: string, option: any) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        popupMatchSelectWidth={false}
                        initialValue={selectedCrops[index].redzoneExceptionId ? selectedCrops[index].redzoneExceptionId : null}
                      >
                        {redzoneExceptions.map((exception, index) => (
                          <Option data-testid={`redzone-exception-id-${index}`} value={exception.id} key={exception.id}>
                            {exception.description}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <Spin />
                    )}
                  </Form.Item>
                </div>
              </div>
            )}
            <div className="crop-input-container">
              {selectedCrops.length > 1 && <div className="empty" />}
              <Flex vertical gap="1rem" justify="space-between" className="full-width">
                <span className="paragraph-m">{t("bauEntryDrawer.crops.yield")}</span>
                <InputNumber
                  dataTestId={`${dataTestId}-yield-input`}
                  name="yield"
                  label={t("bauEntryDrawer.crops.yield")}
                  rules={[{ required: false }]}
                  initialValue={selectedCrops[index].yield}
                  step={0.01}
                  min={0}
                  suffix="t/ha"
                />
              </Flex>
            </div>
          </div>
        </Form>
        {showMultipleMainCropsWarning ? <p className="red-text paragraph-s margin-top-50">{t("bauEntryDrawer.crops.multipleMainCropsWarning")}</p> : null}
      </div>
      {!isAdmin && <ExtraInfo type="neutral-crops-payout-explainer" visible={showNeutralCropsInfoDrawer} setVisible={setShowNeutralCropsInfoDrawer} />}
    </>
  );
};
